/**
 * App.tsx — root component
 *
 * Initializes wallet-core, SessionKit, and renders the correct screen.
 * Screen routing is state-based (no URL router — mobile app pattern).
 */

import { useEffect, useRef, useState } from 'react'
import * as pako from 'pako'
import { WalletPluginEmbedded } from '@properwallet/wallet-core'
import { initSessionKit } from '@properwallet/chain-eos'
import { useWalletStore } from './store/wallet.store'
import { useMechatUnread } from './hooks/useMechatUnread'
import { usePushNotifications } from './hooks/usePushNotifications'

// Screens
import SplashScreen from './screens/SplashScreen'
import OnboardingScreen from './screens/OnboardingScreen'
import SetupPinScreen from './screens/SetupPinScreen'
import PinScreen from './screens/PinScreen'
import WalletScreen from './screens/WalletScreen'
import TransferScreen from './screens/TransferScreen'
import DiscoveryScreen from './screens/DiscoveryScreen'
import GovernanceScreen from './screens/GovernanceScreen'
import SettingsScreen from './screens/SettingsScreen'
import AccountsScreen from './screens/AccountsScreen'
import ResourcesScreen from './screens/ResourcesScreen'
import MechatScreen from './screens/MechatScreen'
import ToolsScreen from './screens/ToolsScreen'
import CreateAccountScreen from './screens/CreateAccountScreen'
import CreateTokenScreen from './screens/CreateTokenScreen'
import KeysScreen from './screens/KeysScreen'
import MultisigScreen from './screens/MultisigScreen'

// pako on window — required before SessionKit init
;(window as unknown as Record<string, unknown>).zlib = pako

// How long the app can be hidden before auto-locking (5 minutes)
const AUTO_LOCK_MS = 5 * 60 * 1000

/** Screens that require an unlocked session */
const LOCKED_SCREENS: readonly string[] = [
  'wallet', 'transfer', 'discovery', 'governance', 'settings', 'accounts', 'resources', 'messages',
  'tools', 'create-account', 'create-token', 'keys', 'multisig',
]

export default function App() {
  const { currentScreen, isSessionLocked, navigate } = useWalletStore()
  const pendingPushTarget = useWalletStore(s => s.pendingPushTarget)
  const [splashVisible, setSplashVisible] = useState(true)
  const [splashOpacity, setSplashOpacity] = useState(true)
  const hiddenAtRef = useRef<number | null>(null)

  // Background watcher for new incoming mechat messages (drives the Chat tab dot)
  useMechatUnread()
  // Registers this device for push + handles notification-tap deep links
  usePushNotifications()

  // A notification tap can arrive after the session is already unlocked (e.g.
  // the 8h session-token auto-restore already ran before the tap event was
  // delivered) — consume it here too, not just right after unlockWithPin/
  // unlockWithPassword, to cover that ordering.
  useEffect(() => {
    if (pendingPushTarget && !isSessionLocked) {
      void useWalletStore.getState().consumePendingPushTarget()
    }
  }, [pendingPushTarget, isSessionLocked])

  // Bootstrap
  useEffect(() => {
    try {
      initSessionKit([new WalletPluginEmbedded()])
    } catch {
      // Already initialized
    }

    void (async () => {
      await Promise.all([
        useWalletStore.getState().init(),
        new Promise<void>(r => setTimeout(r, 1500)),
      ])
      await useWalletStore.getState().resolveRouteAfterBoot()
      setSplashOpacity(false)
      setTimeout(() => setSplashVisible(false), 500)
    })()
  }, [])

  // Auto-lock when app goes to background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now()
      } else {
        const hiddenAt = hiddenAtRef.current
        hiddenAtRef.current = null
        if (hiddenAt !== null && Date.now() - hiddenAt >= AUTO_LOCK_MS) {
          const { isSessionLocked: locked, lockSession, navigate: nav } = useWalletStore.getState()
          if (!locked) {
            lockSession()
            nav('pin')
          }
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // Guard: if session is locked but on a protected screen, redirect to pin
  useEffect(() => {
    if (!splashVisible && isSessionLocked && LOCKED_SCREENS.includes(currentScreen)) {
      navigate('pin')
    }
  }, [splashVisible, isSessionLocked, currentScreen, navigate])

  if (splashVisible) {
    return (
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-500 ${
          splashOpacity ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <SplashScreen />
      </div>
    )
  }

  // Screen router
  const screen = (() => {
    switch (currentScreen) {
      case 'onboarding':
        return <OnboardingScreen />
      case 'setup-pin':
        return <SetupPinScreen />
      case 'pin':
        return <PinScreen />
      case 'wallet':
        return <WalletScreen />
      case 'transfer':
        return <TransferScreen />
      case 'discovery':
        return <DiscoveryScreen />
      case 'governance':
        return <GovernanceScreen />
      case 'settings':
        return <SettingsScreen />
      case 'accounts':
        return <AccountsScreen />
      case 'resources':
        return <ResourcesScreen />
      case 'messages':
        return <MechatScreen />
      case 'tools':
        return <ToolsScreen />
      case 'create-account':
        return <CreateAccountScreen />
      case 'create-token':
        return <CreateTokenScreen />
      case 'keys':
        return <KeysScreen />
      case 'multisig':
        return <MultisigScreen />
      default:
        return <WalletScreen />
    }
  })()

  return (
    <div className="flex flex-col h-screen bg-surface overflow-hidden">
      {screen}
    </div>
  )
}
